import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Facebook, Mail, MapPin, MessageSquareText, Phone, Send, User } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { PhotoUpload } from "../components/PhotoUpload";
import { API, BUSINESS } from "../lib/site";

const inputCls =
  "w-full border border-white/15 bg-white/5 px-4 py-3 text-base text-white placeholder:text-white/40 transition-colors duration-150 focus:border-ves-green focus:outline-none focus:ring-2 focus:ring-ves-green/40";

export default function Contact() {
  const [form, setForm] = useState({ name: "", contact: "", selling: "", message: "", website: "" });
  const [photos, setPhotos] = useState([]);
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      photos.forEach((p) => fd.append("photos", p));
      await axios.post(`${API}/contact`, fd);
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      toast.error(err.response?.data?.detail || "Submission failed — please call us instead.");
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact Velocity Electrical Surplus | Call (586) 718-3404"
        description="Have electrical equipment or wire to sell? Call or text (586) 718-3404, email velocityelectricsurplus@gmail.com, or send your equipment details for a free cash offer."
      />

      <section className="border-b border-white/10 bg-ves-charcoal/40">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <h1 className="font-heading text-4xl font-black text-white sm:text-5xl">
            CONTACT VELOCITY ELECTRICAL SURPLUS
          </h1>
          <p className="mt-4 font-heading text-lg font-bold tracking-wide text-ves-green">
            HAVE ELECTRICAL EQUIPMENT OR WIRE TO SELL? WE WANT TO HEAR FROM YOU.
          </p>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-white/75">
            Whether you have a few pallets of electrical equipment, large quantities of copper
            wire, circuit breakers, transformers, or an entire warehouse of electrical surplus,
            contact Velocity Electrical Surplus for a purchasing offer.
          </p>
        </div>
      </section>

      <section className="bg-ves-black py-16 sm:py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          {/* Contact info + big buttons */}
          <div>
            <div className="border border-white/10 bg-ves-charcoal/50 p-6 sm:p-8">
              <h2 className="font-heading text-xl font-black text-white">
                {BUSINESS.nameUpper}
              </h2>
              <ul className="mt-5 space-y-3 text-sm text-white/80">
                <li className="flex items-center gap-3">
                  <User className="h-5 w-5 shrink-0 text-ves-green" />
                  Purchasing Manager: <strong className="text-white">{BUSINESS.manager}</strong>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="h-5 w-5 shrink-0 text-ves-green" /> {BUSINESS.phoneDisplay}
                </li>
                <li className="flex items-center gap-3 break-all">
                  <Mail className="h-5 w-5 shrink-0 text-ves-green" /> {BUSINESS.email}
                </li>
                <li className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 shrink-0 text-ves-green" /> {BUSINESS.address}
                </li>
                <li className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 shrink-0 text-ves-green" /> Purchasing Area: Nationwide — All 50 States
                </li>
              </ul>
              <a
                href={BUSINESS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-sm font-semibold text-white transition-colors duration-150 hover:border-ves-green hover:text-ves-green"
                data-testid="contact-facebook-link"
              >
                <Facebook className="h-4 w-4" /> Facebook Business Page
              </a>
            </div>

            <div className="mt-6 space-y-3">
              <a
                href={BUSINESS.phoneTel}
                className="flex items-center justify-center gap-3 bg-ves-green px-6 py-5 font-heading text-lg font-black tracking-wide text-ves-black transition-colors duration-150 hover:bg-white"
                data-testid="contact-call-button"
              >
                <Phone className="h-6 w-6" />
                <span className="text-left leading-tight">
                  CALL OUR PURCHASING MANAGER
                  <span className="block text-sm font-bold">{BUSINESS.phoneDisplay}</span>
                </span>
              </a>
              <a
                href={BUSINESS.sms}
                className="flex items-center justify-center gap-3 border-2 border-ves-green px-6 py-5 font-heading text-lg font-black tracking-wide text-ves-green transition-colors duration-150 hover:bg-ves-green hover:text-ves-black"
                data-testid="contact-text-button"
              >
                <MessageSquareText className="h-6 w-6" />
                <span className="text-left leading-tight">
                  TEXT PHOTOS TO US
                  <span className="block text-sm font-bold">{BUSINESS.phoneDisplay}</span>
                </span>
              </a>
              <a
                href={BUSINESS.emailHref}
                className="flex items-center justify-center gap-3 border-2 border-white/25 px-6 py-5 font-heading text-lg font-black tracking-wide text-white transition-colors duration-150 hover:border-white hover:bg-white hover:text-ves-black"
                data-testid="contact-email-button"
              >
                <Mail className="h-6 w-6" />
                <span className="text-left leading-tight">
                  EMAIL VELOCITY ELECTRICAL SURPLUS
                  <span className="block break-all text-sm font-bold">{BUSINESS.email}</span>
                </span>
              </a>
            </div>

            <div className="mt-6 overflow-hidden border border-white/10">
              <iframe
                title="Velocity Electrical Surplus — 51550 Birch St, New Baltimore, MI 48047"
                src={BUSINESS.mapsEmbed}
                className="h-72 w-full grayscale-[35%]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
          </div>

          {/* Simple form */}
          <div className="border border-white/10 bg-ves-charcoal/50 p-6 sm:p-8">
            <h2 className="font-heading text-xl font-black text-white">GET A FREE CASH OFFER</h2>
            <p className="mt-2 text-sm text-white/60">
              Quick and simple — no long forms. We respond fast.
            </p>
            {status === "success" ? (
              <div className="mt-6 border border-ves-green/50 bg-ves-forest/40 p-6 text-center" data-testid="contact-success">
                <CheckCircle2 className="mx-auto h-10 w-10 text-ves-green" />
                <p className="mt-3 font-heading text-lg font-black text-white">
                  YOUR INFORMATION HAS BEEN SENT.
                </p>
                <p className="mt-2 text-sm text-white/75">
                  Thank you — we&rsquo;ll review your submission and respond shortly. Need a
                  faster answer? Call {BUSINESS.manager} at {BUSINESS.phoneDisplay}.
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="contact-form">
                <div>
                  <label htmlFor="c-name" className="mb-1.5 block text-sm font-semibold text-white/80">Name *</label>
                  <input id="c-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" data-testid="contact-name-input" />
                </div>
                <div>
                  <label htmlFor="c-contact" className="mb-1.5 block text-sm font-semibold text-white/80">Phone Number or Email Address *</label>
                  <input id="c-contact" required value={form.contact} onChange={set("contact")} className={inputCls} placeholder="(555) 555-5555 or you@email.com" data-testid="contact-contact-input" />
                </div>
                <div>
                  <label htmlFor="c-selling" className="mb-1.5 block text-sm font-semibold text-white/80">What Are You Selling?</label>
                  <input id="c-selling" value={form.selling} onChange={set("selling")} className={inputCls} placeholder="e.g. 3 pallets of Square D breakers, wire reels…" data-testid="contact-selling-input" />
                </div>
                <div>
                  <label htmlFor="c-message" className="mb-1.5 block text-sm font-semibold text-white/80">Message</label>
                  <textarea id="c-message" rows={4} value={form.message} onChange={set("message")} className={inputCls} placeholder="Quantities, condition, location…" data-testid="contact-message-input" />
                </div>
                <input type="text" name="website" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                <div>
                  <span className="mb-1.5 block text-sm font-semibold text-white/80">Photos (optional)</span>
                  <PhotoUpload files={photos} setFiles={setPhotos} idPrefix="contact-photo" />
                </div>
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="flex w-full items-center justify-center gap-2 bg-ves-green px-6 py-4 font-heading text-base font-black tracking-wide text-ves-black transition-colors duration-150 hover:bg-white disabled:opacity-60"
                  data-testid="contact-submit-button"
                >
                  <Send className="h-5 w-5" />
                  {status === "sending" ? "SENDING…" : "SEND YOUR INFORMATION"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 bg-ves-forest/50 py-14 text-center">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <h2 className="font-heading text-2xl font-black text-white sm:text-3xl">
            READY TO SELL YOUR ELECTRICAL SURPLUS?
          </h2>
          <p className="mt-3 text-lg text-white/85">
            CALL {BUSINESS.manager.toUpperCase()} AT{" "}
            <a href={BUSINESS.phoneTel} className="font-black text-ves-green underline decoration-2 underline-offset-4" data-testid="contact-bottom-call">
              {BUSINESS.phoneDisplay}
            </a>
          </p>
        </div>
      </section>
    </div>
  );
}
